/** Minimal modal dialogs — used for naming a project and for file conflicts. */

export interface DialogChoice {
  id: string;
  label: string;
  primary?: boolean;
}

export interface TextPromptOptions {
  title: string;
  label: string;
  value?: string;
  confirm?: string;
}

export interface ChoicePromptOptions {
  title: string;
  message: string;
  choices: DialogChoice[];
}

function shell(title: string): { overlay: HTMLDivElement; card: HTMLDivElement; close(): void } {
  const overlay = document.createElement('div');
  overlay.className = 'modal';

  const card = document.createElement('div');
  card.className = 'modal-card';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');

  const heading = document.createElement('h2');
  heading.className = 'modal-title';
  heading.textContent = title;
  card.append(heading);
  overlay.append(card);
  document.body.append(overlay);

  const close = (): void => {
    overlay.remove();
  };

  overlay.addEventListener('pointerdown', (event) => {
    if (event.target === overlay) close();
  });
  overlay.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  });

  return { overlay, card, close };
}

/** Asks for a single line of text; resolves `null` when cancelled. */
export function askText(options: TextPromptOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const { card, close } = shell(options.title);
    let settled = false;

    const label = document.createElement('label');
    label.className = 'modal-field';
    label.textContent = options.label;

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'modal-input';
    input.value = options.value ?? '';
    input.spellcheck = false;
    label.append(input);

    const actions = document.createElement('div');
    actions.className = 'modal-actions';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'tool';
    cancel.textContent = 'Cancel';
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'tool primary';
    confirm.textContent = options.confirm ?? 'OK';
    actions.append(cancel, confirm);

    const finish = (value: string | null): void => {
      if (settled) return;
      settled = true;
      close();
      resolve(value);
    };

    cancel.addEventListener('click', () => finish(null));
    confirm.addEventListener('click', () => finish(input.value.trim() || null));
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        finish(input.value.trim() || null);
      }
    });

    card.append(label, actions);
    input.focus();
    input.select();
  });
}

/** Multiple-choice prompt; resolves the chosen id, or `null` when cancelled. */
export function askChoice(options: ChoicePromptOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const { card, close } = shell(options.title);
    let settled = false;

    const message = document.createElement('p');
    message.className = 'modal-message';
    message.textContent = options.message;

    const actions = document.createElement('div');
    actions.className = 'modal-actions';

    const finish = (value: string | null): void => {
      if (settled) return;
      settled = true;
      close();
      resolve(value);
    };

    for (const choice of options.choices) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = choice.primary ? 'tool primary' : 'tool';
      button.textContent = choice.label;
      button.addEventListener('click', () => finish(choice.id));
      actions.append(button);
    }

    card.append(message, actions);
    const first = actions.querySelector<HTMLButtonElement>('button');
    first?.focus();
  });
}
